import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { type Column, DataTable } from '../../components/ui/DataTable.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';

/**
 * 207 (#313): started as a fixed 5 roles, so `pagination` is present (page 1 of 1, per
 * `.claude`/tests/pages/listControlsCoverage.test.tsx's structural consistency requirement) but
 * never advances — 285 (#313 follow-up) lets a SuperAdmin grow this list, but the set stays small
 * enough that pagination still never needs to. No search box — exempted in that same coverage
 * test with a stated reason, since there is nothing meaningful to filter among a small role list.
 * `editable` decides whether a row's detail page shows a checkbox grid or a read-only introspected
 * rule list — `RoleDetailPage.tsx` reads the SAME flag the backend computed, never re-derives it.
 */

interface RoleSummary {
  id: string;
  name: string;
  description: string | null;
  is_system: boolean;
  editable: boolean;
}

export function RolesListPage() {
  const ability = useAbility();
  const { data, isLoading, error } = useQuery({
    queryKey: RESOURCE_KEYS.role.list,
    queryFn: () => api.get<{ items: RoleSummary[] }>('/roles'),
    retry: false,
  });

  const columns: Column<RoleSummary>[] = [
    {
      key: 'name',
      header: 'Role',
      render: (row) => (
        <Link to={`/roles/${row.id}`} className="text-primary hover:underline">
          {row.name}
        </Link>
      ),
    },
    { key: 'description', header: 'Description', render: (row) => row.description ?? '—' },
    {
      key: 'editable',
      header: 'Permissions',
      render: (row) => (
        <Badge variant={row.editable ? 'default' : 'success'}>
          {row.editable ? 'Editable' : 'Protected'}
        </Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Roles"
        description="Who can do what. SuperAdmin and Customer are protected — every other role's permissions can be reassigned here."
        actions={
          ability.can('manage', 'Role') && (
            <Link to="/roles/new">
              <Button>+ New Role</Button>
            </Link>
          )
        }
      />
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        keyExtractor={(row) => row.id}
        emptyMessage="No roles found"
        error={error as { status?: number } | null}
        pagination={{ page: 1, totalPages: 1, onPageChange: () => {} }}
      />
    </div>
  );
}
